import { defineComponent as _defineComponent } from 'vue'
import { ref, Ref, computed, WritableComputedRef, PropType, watch } from 'vue';
import {
    ConnectFlowModelingActions,
    ConnectFlowModelingGetters,
    ConnectFlowModelingMutations,
    ConnectFlowAudioModel,
    VueFlowNodeModel,
} from '@/types';
import store from '@/store';
import { QuestionMarkCircleIcon } from '@heroicons/vue/outline';
import { Handle, Position } from '@vue-flow/core';
import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import UiTextInput from '@/components/UiTextInput.vue';

interface IClientInfoFormState {
    audio: null | ConnectFlowAudioModel;
    text: undefined | string;
    timeout: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ClientInfo',
  props: {
    node: {
        type: Object as PropType<VueFlowNodeModel>,
        required: true,
    },
},
  setup(__props, { expose: __expose }) {
  __expose();

const { t } = useI18n();

const props = __props;

const showForm = ref(false);

const nodeWritable: WritableComputedRef<VueFlowNodeModel> = computed({
    get: () => store.getters['flow/' + ConnectFlowModelingGetters.ELEMENT_BY_ID](props.node.id),
    set: (value: VueFlowNodeModel) => {
        store.commit('flow/' + ConnectFlowModelingMutations.UPDATE_ELEMENT, {
            idx: null,
            value,
        });
    },
});

const audioOptions = computed(
    () => store.getters['flow/' + ConnectFlowModelingGetters.AUDIO_OPTIONS],
);
const formState: Ref<IClientInfoFormState> = ref({
    audio: null,
    text: undefined,
    timeout: 10,
});

const textValidation = t('connectFlow.node.clientInfo.validation');
const atLeastOneFilled = helpers.withMessage(textValidation, (value, state) => {
    return !!state.audio || !!state.text;
});
const rules = computed<ValidationArgs<IClientInfoFormState>>(() => {
    const r: ValidationArgs<IClientInfoFormState> = {
        audio: {
            atLeastOneFilled,
        },
        text: {
            atLeastOneFilled,
        },
        timeout: {},
    };
    return r;
});
const v$: Ref<Validation<ValidationArgs<IClientInfoFormState>>> = useVuelidate(rules, formState);

const nodeText = computed(() => {
    let text = t('connectFlow.node.clientInfo.empty');
    if (nodeWritable.value.data.related) {
        text = nodeWritable.value.data.related.label;
    } else if (nodeWritable.value.data.textToAudio) {
        text = t('connectFlow.node.clientInfo.textToAudio');
    }
    return text;
});

const isFormCorrect = ref(true);

function removeNode() {
    store.dispatch('flow/' + ConnectFlowModelingActions.DELETE_NODE, {
        nodeId: nodeWritable.value.id,
        parentId: nodeWritable.value.parentId,
    });
}
async function onSubmit() {
    isFormCorrect.value = await v$.value.$validate();
    if (isFormCorrect.value) {
        if (formState.value.audio) {
            nodeWritable.value.data.related = {
                value: formState.value.audio.id,
                label: formState.value.audio.name,
            };
            nodeWritable.value.data.textToAudio = undefined;
        } else if (formState.value.text) {
            nodeWritable.value.data.textToAudio = formState.value.text;
            nodeWritable.value.data.related = null;
        }
        showForm.value = false;
    }
    nodeWritable.value.data.hasErrors = !isFormCorrect.value;
    v$.value.$reset();
}
watch([nodeWritable.value], () => {
    formState.value.audio = audioOptions.value.find(
        (audio: ConnectFlowAudioModel) => audio.id == nodeWritable.value.data.related?.value,
    );
    formState.value.text = nodeWritable.value.data.textToAudio;

    v$.value.$validate().then((result) => {
        isFormCorrect.value = result;
        nodeWritable.value.data.hasErrors = !isFormCorrect.value;
    });
});

const __returned__ = { t, props, showForm, nodeWritable, audioOptions, formState, textValidation, atLeastOneFilled, rules, v$, nodeText, isFormCorrect, removeNode, onSubmit, get QuestionMarkCircleIcon() { return QuestionMarkCircleIcon }, get Handle() { return Handle }, get Position() { return Position }, UiTextInput }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})